import { useState } from 'react'

import { Box, Tabs } from '@factorialco/deprecated-design-system'
import { isSameDay } from 'date-fns'
import {
  InsightsHeadcountFragment,
  InsightsJoinerFragment,
  InsightsLeaverFragment,
} from 'generated/resources/graphql'

import DataTable from 'modules/core/components/DataTable'
import ExportModal from 'modules/core/components/DataTable/core/HeadBar/ExportModal'
import { parseBackendDate } from 'modules/core/components/DateNavigator/dateFormatUtils'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import {
  HeadcountEvolutionData,
  findDataFor,
} from 'modules/insights/components/WidgetHeadcountEvolution/ChartHeadcountEvolution/headcountEvolutionUtils'
import { HeadcountDateRangeNavigator } from './HeadcountDateRangeNavigator'
import { HeadcountViewContentTab, useHeadcountDatatableSource } from './useHeadcountDatatableSource'

type Props = {
  data: HeadcountEvolutionData
  headcountEvolution: InsightsHeadcountFragment[]
  joiners: InsightsJoinerFragment[]
  leavers: InsightsLeaverFragment[]
  selectedRange: Date | undefined
  showExportModal?: boolean
  onChangeSelectedRange: (date: Date) => void
  onCloseExportModal?: () => void
}

export const HeadcountTable = ({
  data,
  headcountEvolution,
  joiners,
  leavers,
  selectedRange,
  showExportModal,
  onChangeSelectedRange,
  onCloseExportModal,
}: Props) => {
  const { navigation } = useNavigation()

  const currentAxisData = useUnsafeMemo(
    () => selectedRange && findDataFor(headcountEvolution, data, selectedRange),
    [headcountEvolution, data, selectedRange]
  )

  const [tab, setTab] = useState<HeadcountViewContentTab>(HeadcountViewContentTab.Headcount)

  const {
    access: { id: accessId },
    company: { id: companyId },
  } = useCurrentSession()
  const handleChangeTab = useUnsafeCallback(
    (newTab: HeadcountViewContentTab) => {
      setTab(newTab)

      track(trackingEvents.insights.granularViewTabChanged, {
        company_id: companyId,
        access_id: accessId,
        tab: newTab,
      })
    },
    [setTab]
  )

  const handleTableExported = useUnsafeCallback(() => {
    track(trackingEvents.insights.headcountPage.export, {
      tab,
    })
  }, [tab])

  const metricsByType = useUnsafeMemo(() => {
    switch (tab) {
      case HeadcountViewContentTab.Headcount:
        return headcountEvolution
      case HeadcountViewContentTab.Joiners:
        return joiners
      case HeadcountViewContentTab.Leavers:
        return leavers
    }
  }, [tab, headcountEvolution, leavers, joiners])

  const employeeIds = useUnsafeMemo(() => {
    if (!currentAxisData) {
      return
    }

    return metricsByType.find((metrics) => {
      const metricsDate = parseBackendDate(metrics.endDate)

      return isSameDay(metricsDate, currentAxisData.endDate)
    })?.employeeIds
  }, [currentAxisData, metricsByType])

  const exportFileName = useUnsafeMemo(() => {
    const getCurrentTabName = (): string => {
      switch (tab) {
        case HeadcountViewContentTab.Headcount:
          return i18n.t('insights.widgets.shared.headcount')
        case HeadcountViewContentTab.Joiners:
          return i18n.t('insights.widgets.headcount_evolution.joiners')
        case HeadcountViewContentTab.Leavers:
          return i18n.t('insights.widgets.headcount_evolution.leavers')
      }
    }

    const getFileExportDate = () => {
      const startDate = currentAxisData?.startDate
      const endDate = currentAxisData?.endDate

      if (!startDate || !endDate) {
        return ''
      }

      return `${dateToString(startDate, 'backend')}_${dateToString(endDate, 'backend')}`
    }

    return `${i18n.t(
      'insights.pages.headcount.title'
    )}_${getFileExportDate()}_${getCurrentTabName()}`
  }, [tab, currentAxisData])

  const dataTableSource = useHeadcountDatatableSource(
    tab,
    employeeIds ?? [],
    selectedRange ?? new Date()
  )

  return (
    <Box gap="s32" height="full">
      <Box flexDirection="row" gap="s40">
        <Box flexGrow>
          <Tabs currentId={tab} onTabClicked={handleChangeTab as (newTab: string) => void}>
            <Tabs.Tab
              id={HeadcountViewContentTab.Headcount}
              label={i18n.t('insights.widgets.shared.headcount')}
            />
            <Tabs.Tab
              id={HeadcountViewContentTab.Joiners}
              label={i18n.t('insights.widgets.headcount_evolution.joiners')}
            />
            <Tabs.Tab
              id={HeadcountViewContentTab.Leavers}
              label={i18n.t('insights.widgets.headcount_evolution.leavers')}
            />
          </Tabs>
        </Box>

        {selectedRange && (
          <HeadcountDateRangeNavigator
            currentDate={selectedRange}
            headcountEvolution={headcountEvolution}
            onRangeChange={onChangeSelectedRange}
          />
        )}
      </Box>

      <Box flexGrow>
        {currentAxisData && (
          <DataTable.Provider
            source={dataTableSource}
            pagination={{
              kind: 'offset',
              itemsPerPage: 10,
            }}
            rowTo={(row) =>
              navigation.employees.id.fullPath({
                id: row.id,
              })
            }
          >
            <DataTable.Table />

            {showExportModal && (
              <ExportModal
                source={dataTableSource}
                fileName={exportFileName}
                loading={false}
                onClose={() => onCloseExportModal?.()}
                onExportCallback={handleTableExported}
              />
            )}
          </DataTable.Provider>
        )}
      </Box>
    </Box>
  )
}
